import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    MdArrowBack, MdMemory, MdEdit, MdDelete, MdAdd,
    MdSave, MdCancel, MdRefresh, MdContentCopy, MdPowerSettingsNew,
    MdLabel, MdToggleOn, MdToggleOff, MdTune, MdScience,
    MdVisibility, MdVisibilityOff, MdWifi, MdWifiOff
} from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';
import SensorCalibrationModal from '../../components/admin/SensorCalibrationModal';

export default function AdminPlcSettingsPage() {
    const { plcId, id: companyId } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [plcCard, setPlcCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditingName, setIsEditingName] = useState(false);
    const [plcNameInput, setPlcNameInput] = useState('');
    const [newOutputName, setNewOutputName] = useState('');
    const [addingOutput, setAddingOutput] = useState(false);
    const [editingOutputId, setEditingOutputId] = useState(null);
    const [editOutputNameInput, setEditOutputNameInput] = useState('');

    // Reference Values state
    const [newRefName, setNewRefName] = useState('');
    const [newRefValue, setNewRefValue] = useState('');
    const [addingRef, setAddingRef] = useState(false);
    const [editingRefId, setEditingRefId] = useState(null);
    const [editRefNameInput, setEditRefNameInput] = useState('');
    const [editRefValueInput, setEditRefValueInput] = useState('');

    // Sensor Calibration state
    const [systemSensors, setSystemSensors] = useState([]);
    const [addingCalibrationSensorId, setAddingCalibrationSensorId] = useState(null);
    const [calibratingEntry, setCalibratingEntry] = useState(null); // { entry, sensorName }

    const fetchPlcData = useCallback(async () => {
        try {
            setLoading(true);
            const plcRes = await api.get(`/plc/company/${companyId}`);
            const list = plcRes.data.data || [];
            const card = list.find(item => item._id === plcId);
            
            if (card) {
                setPlcCard(card);
                setPlcNameInput(card.plcName);
            } else {
                toast.error(t('companyDetail.notFound'));
                navigate(-1);
            }
        } catch (err) {
            console.error('Fetch PLC details error:', err);
            toast.error(t('companyDetail.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [plcId, companyId, navigate, t]);

    useEffect(() => {
        fetchPlcData();
        
        // Polling to update last active / state of card dynamically every 10s
        const interval = setInterval(() => {
            api.get(`/plc/company/${companyId}`)
                .then(res => {
                    const list = res.data.data || [];
                    const card = list.find(item => item._id === plcId);
                    if (card) {
                        setPlcCard(card);
                    }
                })
                .catch(() => {});
        }, 10000);

        return () => clearInterval(interval);
    }, [fetchPlcData, plcId, companyId]);

    useEffect(() => {
        const systemId = plcCard?.systemId?._id;
        if (!systemId) return;
        api.get(`/sensors/system/${systemId}`)
            .then(res => setSystemSensors(res.data.data || []))
            .catch(() => {});
    }, [plcCard?.systemId?._id]);

    const handleCopyUUID = () => {
        if (!plcCard) return;
        navigator.clipboard.writeText(plcCard.plcUUID);
        toast.success(t('sensorDetail.idCopied'));
    };

    const handleUpdatePlcName = async () => {
        if (!plcNameInput.trim()) {
            toast.error(t('plc.plcNameRequired'));
            return;
        }
        try {
            const res = await api.put(`/plc/${plcId}`, { plcName: plcNameInput.trim() });
            setPlcCard(prev => ({ ...prev, plcName: res.data.data.plcName }));
            setIsEditingName(false);
            toast.success(t('plc.plcUpdated'));
        } catch (err) {
            toast.error(t('renameModal.updateFailed'));
        }
    };

    const handleDeletePlcCard = async () => {
        if (!window.confirm(t('plc.deletePlcConfirm'))) return;
        try {
            await api.delete(`/plc/${plcId}`);
            toast.success(t('plc.plcDeleted'));
            navigate(-1);
        } catch (err) {
            toast.error(t('common.delete') + ' failed');
        }
    };

    const handleRestartDevice = async () => {
        if (!window.confirm(t('plc.restartConfirm'))) return;
        try {
            await api.post(`/plc/${plcId}/restart`);
            toast.success(t('plc.restartQueued'));
        } catch (err) {
            toast.error(t('plc.restartDevice') + ' failed');
        }
    };

    const handleToggleManual = async () => {
        try {
            const newVal = !plcCard.manual;
            const res = await api.put(`/plc/${plcId}/manual`, { manual: newVal });
            setPlcCard(res.data.data);
            toast.success(t(newVal ? 'plc.manualEnabled' : 'plc.manualDisabled'));
        } catch (err) {
            toast.error(t('plc.manualUpdateFailed'));
        }
    };

    const handleToggleRemoteAccess = async () => {
        try {
            const newVal = !plcCard.remoteAccessEnabled;
            const res = await api.put(`/plc/${plcId}/remote-access`, { remoteAccessEnabled: newVal });
            setPlcCard(res.data.data);
            toast.success(t(newVal ? 'plc.remoteAccessEnabled' : 'plc.remoteAccessDisabled'));
        } catch (err) {
            toast.error(t('plc.remoteAccessUpdateFailed'));
        }
    };

    const handleToggleCalibration = async () => {
        try {
            const newVal = !plcCard.calibration;
            const res = await api.put(`/plc/${plcId}/calibration`, { calibration: newVal });
            setPlcCard(res.data.data);
            toast.success(t(newVal ? 'plc.calibrationEnabled' : 'plc.calibrationDisabled'));
        } catch (err) {
            toast.error(t('plc.calibrationUpdateFailed'));
        }
    };

    const handleToggleCalibrationSensor = async (sensor, isSelected, entryId) => {
        if (isSelected) {
            if (!window.confirm(t('plc.calibrationSensorRemoveConfirm'))) return;
            try {
                const res = await api.delete(`/plc/${plcId}/calibration/sensors/${entryId}`);
                setPlcCard(res.data.data);
                toast.success(t('plc.calibrationSensorRemoved'));
            } catch (err) {
                toast.error(err.response?.data?.message || t('plc.calibrationSensorRemoveFailed'));
            }
        } else {
            try {
                setAddingCalibrationSensorId(sensor._id);
                const res = await api.post(`/plc/${plcId}/calibration/sensors`, { sensorId: sensor._id });
                setPlcCard(res.data.data);
                toast.success(t('plc.calibrationSensorAdded'));
            } catch (err) {
                toast.error(err.response?.data?.message || t('plc.calibrationSensorAddFailed'));
            } finally {
                setAddingCalibrationSensorId(null);
            }
        }
    };

    const countActiveCalibrationSteps = (entry) => {
        if (!entry) return 0;
        return entry.steps.filter(step => {
            if (!step.startedAt) return false;
            const elapsed = (Date.now() - new Date(step.startedAt).getTime()) / 1000;
            return step.duration - elapsed > 0;
        }).length;
    };

    const handleCalibrationUpdate = (updatedCard) => {
        setPlcCard(updatedCard);
        setCalibratingEntry(prev => {
            if (!prev) return prev;
            const updatedEntry = updatedCard.calibrationSensors.find(e => e._id === prev.entry._id);
            return updatedEntry ? { ...prev, entry: updatedEntry } : null;
        });
    };

    const handleToggleOutputVisibility = async (outputId) => {
        try {
            const res = await api.put(`/plc/${plcId}/outputs/${outputId}/visibility`);
            setPlcCard(res.data.data);
            toast.success(t('plc.visibilityUpdated'));
        } catch (err) {
            toast.error(t('plc.visibilityUpdateFailed'));
        }
    };

    const handleToggleRefVisibility = async (refId) => {
        try {
            const res = await api.put(`/plc/${plcId}/reference-values/${refId}/visibility`);
            setPlcCard(res.data.data);
            toast.success(t('plc.visibilityUpdated'));
        } catch (err) {
            toast.error(t('plc.visibilityUpdateFailed'));
        }
    };

    const handleAddOutput = async (e) => {
        e.preventDefault();
        if (!newOutputName.trim()) {
            toast.error(t('plc.plcNameRequired')); // Output name required
            return;
        }
        try {
            setAddingOutput(true);
            const res = await api.post(`/plc/${plcId}/outputs`, { name: newOutputName.trim() });
            setPlcCard(res.data.data);
            setNewOutputName('');
            toast.success(t('plc.outputAdded'));
        } catch (err) {
            toast.error(t('common.add') + ' failed');
        } finally {
            setAddingOutput(false);
        }
    };

    const handleToggleOutput = async (outputId, currentState) => {
        try {
            const res = await api.put(`/plc/${plcId}/outputs/${outputId}`, { state: !currentState });
            setPlcCard(res.data.data);
            toast.success(t('plc.outputUpdated'));
        } catch (err) {
            toast.error(t('companyDetail.settingFailed'));
        }
    };

    const handleUpdateOutputName = async (outputId) => {
        if (!editOutputNameInput.trim()) {
            toast.error(t('renameModal.nameRequired'));
            return;
        }
        try {
            const res = await api.put(`/plc/${plcId}/outputs/${outputId}`, { name: editOutputNameInput.trim() });
            setPlcCard(res.data.data);
            setEditingOutputId(null);
            toast.success(t('plc.outputUpdated'));
        } catch (err) {
            toast.error(t('renameModal.updateFailed'));
        }
    };

    const handleDeleteOutput = async (outputId) => {
        if (!window.confirm(t('common.delete') + '?')) return;
        try {
            const res = await api.delete(`/plc/${plcId}/outputs/${outputId}`);
            setPlcCard(res.data.data);
            toast.success(t('plc.outputDeleted'));
        } catch (err) {
            toast.error(t('common.delete') + ' failed');
        }
    };

    const handleAddRefValue = async (e) => {
        e.preventDefault();
        if (!newRefName.trim() || !newRefValue.trim()) {
            toast.error(t('plc.referenceValueName') + ' / ' + t('plc.referenceValueVal') + ' zorunludur');
            return;
        }
        try {
            setAddingRef(true);
            const res = await api.post(`/plc/${plcId}/reference-values`, { name: newRefName.trim(), value: newRefValue.trim() });
            setPlcCard(res.data.data);
            setNewRefName('');
            setNewRefValue('');
            toast.success(t('plc.referenceValueAdded'));
        } catch (err) {
            toast.error(t('common.add') + ' failed');
        } finally {
            setAddingRef(false);
        }
    };

    const handleUpdateRefValue = async (refId) => {
        if (!editRefNameInput.trim() || !editRefValueInput.trim()) {
            toast.error(t('renameModal.nameRequired'));
            return;
        }
        try {
            const res = await api.put(`/plc/${plcId}/reference-values/${refId}`, { name: editRefNameInput.trim(), value: editRefValueInput.trim() });
            setPlcCard(res.data.data);
            setEditingRefId(null);
            toast.success(t('plc.referenceValueUpdated'));
        } catch (err) {
            toast.error(t('renameModal.updateFailed'));
        }
    };

    const handleDeleteRefValue = async (refId) => {
        if (!window.confirm(t('plc.deleteReferenceValueConfirm'))) return;
        try {
            const res = await api.delete(`/plc/${plcId}/reference-values/${refId}`);
            setPlcCard(res.data.data);
            toast.success(t('plc.referenceValueDeleted'));
        } catch (err) {
            toast.error(t('common.delete') + ' failed');
        }
    };

    const isOnline = () => {
        if (!plcCard || !plcCard.lastActive) return false;
        const diff = Date.now() - new Date(plcCard.lastActive).getTime();
        return diff < 20000; // Active within last 20 seconds
    };

    if (loading && !plcCard) return <div className="loading-page"><div className="spinner"></div></div>;
    if (!plcCard) return <div className="loading-page"><p>{t('companyDetail.notFound')}</p></div>;

    return (
        <div className="page-wrapper">
            {/* Header */}
            <div className="page-header" style={{ marginBottom: 32 }}>
                <div className="page-header-left">
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate(-1)} id="back-btn" style={{ marginRight: 16 }}>
                        <MdArrowBack />
                    </button>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                            {!isEditingName ? (
                                <>
                                    <h1 className="page-title" style={{ fontSize: 26, margin: 0 }}>{plcCard.plcName}</h1>
                                    <button onClick={() => setIsEditingName(true)} className="btn btn-link btn-xs" style={{ display: 'flex', color: 'var(--text-muted)' }}>
                                        <MdEdit size={16} />
                                    </button>
                                </>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input 
                                        className="form-input form-input-sm" 
                                        value={plcNameInput} 
                                        onChange={e => setPlcNameInput(e.target.value)} 
                                        style={{ fontSize: 18, width: 200 }}
                                    />
                                    <button onClick={handleUpdatePlcName} className="btn btn-success btn-xs" style={{ height: 32, padding: '0 8px' }}>
                                        <MdSave />
                                    </button>
                                    <button onClick={() => { setIsEditingName(false); setPlcNameInput(plcCard.plcName); }} className="btn btn-secondary btn-xs" style={{ height: 32, padding: '0 8px' }}>
                                        <MdCancel />
                                    </button>
                                </div>
                            )}

                            {/* Status Indicator */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border)', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, marginLeft: 8 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline() ? 'var(--success)' : 'var(--text-muted)', boxShadow: isOnline() ? '0 0 8px var(--success)' : 'none' }}></div>
                                <span style={{ color: isOnline() ? 'var(--success)' : 'var(--text-secondary)' }}>
                                    {isOnline() ? 'PLC KART AKTİF' : 'PLC KART PASİF'}
                                </span>
                            </div>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                            {t('common.system')}: <strong style={{ color: 'var(--text-primary)' }}>{plcCard.systemId?.systemName || '—'}</strong>
                        </div>
                    </div>
                </div>
                <div className="page-header-actions">
                    <button className="btn btn-secondary btn-sm" onClick={fetchPlcData}>
                        <MdRefresh /> {t('common.refresh')}
                    </button>
                    <button className="btn btn-warning btn-sm" onClick={handleRestartDevice} style={{ border: 'none', background: 'var(--warning)', color: 'white' }}>
                        <MdPowerSettingsNew /> {t('plc.restartDevice')}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={handleDeletePlcCard}>
                        <MdDelete /> {t('common.delete')}
                    </button>
                </div>
            </div>

            <div className="plc-settings-grid" style={{ maxWidth: 1400, margin: '0 auto', alignItems: 'start' }}>
                
                {/* Left Card: Info & Integration */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div className="card" style={{ padding: 20 }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MdMemory style={{ color: 'var(--primary)' }} /> {t('plc.plcCard')}
                        </h3>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('plc.plcUUID')}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-body)', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)' }}>
                                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-primary)', wordBreak: 'break-all', flex: 1 }}>{plcCard.plcUUID}</span>
                                    <button onClick={handleCopyUUID} className="btn btn-secondary btn-xs btn-icon" style={{ flexShrink: 0 }}>
                                        <MdContentCopy size={13} />
                                    </button>
                                </div>
                            </div>

                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('plc.lastSync')}</div>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>
                                    {plcCard.lastActive ? new Date(plcCard.lastActive).toLocaleString() : t('plc.notSyncedYet')}
                                </div>
                            </div>

                            {/* Manual Mode Switch */}
                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10 }}>
                                    {t('plc.manualMode')}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <button
                                        id="manual-mode-toggle"
                                        onClick={handleToggleManual}
                                        style={{
                                            position: 'relative',
                                            width: 52,
                                            height: 28,
                                            borderRadius: 14,
                                            border: 'none',
                                            cursor: 'pointer',
                                            background: plcCard.manual ? 'var(--success)' : 'var(--text-muted)',
                                            transition: 'background 0.25s',
                                            flexShrink: 0,
                                            padding: 0,
                                            boxShadow: plcCard.manual ? '0 0 10px rgba(34,197,94,0.4)' : 'none'
                                        }}
                                        title={t('plc.manualMode')}
                                    >
                                        <span style={{
                                            position: 'absolute',
                                            top: 4,
                                            left: plcCard.manual ? 28 : 4,
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            background: 'white',
                                            transition: 'left 0.25s',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                                        }} />
                                    </button>
                                    <span style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: plcCard.manual ? 'var(--success)' : '#ef4444',
                                        background: plcCard.manual ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.10)',
                                        border: `1px solid ${plcCard.manual ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.25)'}`,
                                        padding: '3px 12px',
                                        borderRadius: 20,
                                        letterSpacing: '0.04em'
                                    }}>
                                        {plcCard.manual ? t('common.yes') : t('common.no')}
                                    </span>
                                </div>
                            </div>

                            {/* Remote Access Switch */}
                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {plcCard.remoteAccessEnabled ? <MdWifi style={{ color: 'var(--success)' }} /> : <MdWifiOff />}
                                    {t('plc.remoteAccess')}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <button
                                        id="remote-access-toggle"
                                        onClick={handleToggleRemoteAccess}
                                        style={{
                                            position: 'relative',
                                            width: 52,
                                            height: 28,
                                            borderRadius: 14,
                                            border: 'none',
                                            cursor: 'pointer',
                                            background: plcCard.remoteAccessEnabled ? 'var(--primary)' : 'var(--text-muted)',
                                            transition: 'background 0.25s',
                                            flexShrink: 0,
                                            padding: 0,
                                            boxShadow: plcCard.remoteAccessEnabled ? '0 0 10px rgba(79,142,247,0.4)' : 'none'
                                        }}
                                        title={t('plc.remoteAccess')}
                                    >
                                        <span style={{
                                            position: 'absolute',
                                            top: 4,
                                            left: plcCard.remoteAccessEnabled ? 28 : 4,
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            background: 'white',
                                            transition: 'left 0.25s',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                                        }} />
                                    </button>
                                    <span style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: plcCard.remoteAccessEnabled ? 'var(--primary)' : '#ef4444',
                                        background: plcCard.remoteAccessEnabled ? 'rgba(79,142,247,0.12)' : 'rgba(239,68,68,0.10)',
                                        border: `1px solid ${plcCard.remoteAccessEnabled ? 'rgba(79,142,247,0.3)' : 'rgba(239,68,68,0.25)'}`,
                                        padding: '3px 12px',
                                        borderRadius: 20,
                                        letterSpacing: '0.04em'
                                    }}>
                                        {plcCard.remoteAccessEnabled ? t('common.yes') : t('common.no')}
                                    </span>
                                </div>
                            </div>

                            {/* Calibration Switch */}
                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <MdScience style={{ color: plcCard.calibration ? 'var(--success)' : 'var(--text-muted)' }} />
                                    {t('plc.calibration')}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <button
                                        id="calibration-toggle"
                                        onClick={handleToggleCalibration}
                                        style={{
                                            position: 'relative',
                                            width: 52,
                                            height: 28,
                                            borderRadius: 14,
                                            border: 'none',
                                            cursor: 'pointer',
                                            background: plcCard.calibration ? 'var(--success)' : 'var(--text-muted)',
                                            transition: 'background 0.25s',
                                            flexShrink: 0,
                                            padding: 0,
                                            boxShadow: plcCard.calibration ? '0 0 10px rgba(34,197,94,0.4)' : 'none'
                                        }}
                                        title={t('plc.calibration')}
                                    >
                                        <span style={{
                                            position: 'absolute',
                                            top: 4,
                                            left: plcCard.calibration ? 28 : 4,
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            background: 'white',
                                            transition: 'left 0.25s',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                                        }} />
                                    </button>
                                    <span style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: plcCard.calibration ? 'var(--success)' : '#ef4444',
                                        background: plcCard.calibration ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.10)',
                                        border: `1px solid ${plcCard.calibration ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.25)'}`,
                                        padding: '3px 12px',
                                        borderRadius: 20,
                                        letterSpacing: '0.04em'
                                    }}>
                                        {plcCard.calibration ? t('common.yes') : t('common.no')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Card: Outputs List */}
                <div className="card" style={{ padding: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{t('plc.outputs')}</h3>
                        <span className="badge badge-primary">{plcCard.outputs?.length || 0} {t('common.records')}</span>
                    </div>

                    {/* Add Output Form */}
                    <form onSubmit={handleAddOutput} style={{ display: 'flex', gap: 12, marginBottom: 20, background: 'var(--bg-elevated)', padding: 12, borderRadius: 12, border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 14, paddingLeft: 4 }}>
                            <MdLabel />
                        </div>
                        <input 
                            className="form-input form-input-sm"
                            placeholder={t('plc.outputNamePlaceholder')}
                            value={newOutputName}
                            onChange={e => setNewOutputName(e.target.value)}
                            disabled={addingOutput}
                            style={{ flex: 1 }}
                        />
                        <button type="submit" className="btn btn-primary btn-sm" disabled={addingOutput} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {addingOutput ? <span className="spinner spinner-sm" /> : <MdAdd />}
                            {t('common.add')}
                        </button>
                    </form>

                    {/* Outputs Table */}
                    {!plcCard.outputs || plcCard.outputs.length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                            {t('plc.noOutputs')}
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('plc.outputName')}</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>ID</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('common.active')} / {t('common.passive')}</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', textAlign: 'center' }}>{t('plc.companyVisible')}</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', textAlign: 'right' }}>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {plcCard.outputs.map((out) => (
                                        <tr key={out._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                                            <td style={{ padding: '14px 8px' }}>
                                                {editingOutputId !== out._id ? (
                                                    <span style={{ fontSize: 14, fontWeight: 600 }}>{out.name}</span>
                                                ) : (
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <input 
                                                            className="form-input form-input-sm" 
                                                            value={editOutputNameInput}
                                                            onChange={e => setEditOutputNameInput(e.target.value)}
                                                            style={{ height: 28, fontSize: 12, padding: '0 8px', width: 150 }}
                                                        />
                                                        <button onClick={() => handleUpdateOutputName(out._id)} className="btn btn-success btn-xs" style={{ height: 28 }}>
                                                            <MdSave />
                                                        </button>
                                                        <button onClick={() => setEditingOutputId(null)} className="btn btn-secondary btn-xs" style={{ height: 28 }}>
                                                            <MdCancel />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 8px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                                                {out._id}
                                            </td>
                                            <td style={{ padding: '14px 8px' }}>
                                                <button 
                                                    onClick={() => handleToggleOutput(out._id, out.state)}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', gap: 6,
                                                        color: out.state ? 'var(--success)' : 'var(--text-muted)',
                                                        padding: 0, fontWeight: 700, fontSize: 13
                                                    }}
                                                >
                                                    {out.state ? (
                                                        <>
                                                            <MdToggleOn size={26} />
                                                            <span>{t('plc.stateOn')}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MdToggleOff size={26} />
                                                            <span>{t('plc.stateOff')}</span>
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td style={{ padding: '14px 8px', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleToggleOutputVisibility(out._id)}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        color: out.companyVisible ? 'var(--primary)' : 'var(--text-muted)',
                                                        padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        margin: '0 auto'
                                                    }}
                                                    title={t('plc.companyVisible')}
                                                >
                                                    {out.companyVisible ? <MdVisibility size={20} /> : <MdVisibilityOff size={20} />}
                                                </button>
                                            </td>
                                            <td style={{ padding: '14px 8px', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                    {editingOutputId !== out._id && (
                                                        <button 
                                                            onClick={() => { setEditingOutputId(out._id); setEditOutputNameInput(out.name); }} 
                                                            className="btn btn-secondary btn-xs btn-icon"
                                                            title={t('common.edit')}
                                                        >
                                                            <MdEdit />
                                                        </button>
                                                    )}
                                                    <button 
                                                        onClick={() => handleDeleteOutput(out._id)} 
                                                        className="btn btn-danger btn-xs btn-icon"
                                                        title={t('common.delete')}
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Reference Values Card */}
                <div className="card" style={{ padding: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MdTune style={{ color: 'var(--primary)' }} />
                            {t('plc.referenceValues')}
                        </h3>
                        <span className="badge badge-primary">{plcCard.referenceValues?.length || 0} {t('common.records')}</span>
                    </div>

                    {/* Add Reference Value Form */}
                    <form onSubmit={handleAddRefValue} style={{ display: 'flex', gap: 12, marginBottom: 20, background: 'var(--bg-elevated)', padding: 12, borderRadius: 12, border: '1px solid var(--border)', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 14, paddingLeft: 4 }}>
                            <MdTune />
                        </div>
                        <input
                            className="form-input form-input-sm"
                            placeholder={t('plc.referenceValueNamePlaceholder')}
                            value={newRefName}
                            onChange={e => setNewRefName(e.target.value)}
                            disabled={addingRef}
                            style={{ flex: 1, minWidth: 160 }}
                        />
                        <input
                            className="form-input form-input-sm"
                            placeholder={t('plc.referenceValueValPlaceholder')}
                            value={newRefValue}
                            onChange={e => setNewRefValue(e.target.value)}
                            disabled={addingRef}
                            style={{ flex: 1, minWidth: 120 }}
                        />
                        <button type="submit" className="btn btn-primary btn-sm" disabled={addingRef} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {addingRef ? <span className="spinner spinner-sm" /> : <MdAdd />}
                            {t('common.add')}
                        </button>
                    </form>

                    {/* Reference Values Table */}
                    {!plcCard.referenceValues || plcCard.referenceValues.length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                            {t('plc.noReferenceValues')}
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('plc.referenceValueName')}</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('plc.referenceValueVal')}</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>ID</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', textAlign: 'center' }}>{t('plc.companyVisible')}</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', textAlign: 'right' }}>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {plcCard.referenceValues.map((ref) => (
                                        <tr key={ref._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                                            <td style={{ padding: '14px 8px' }}>
                                                {editingRefId !== ref._id ? (
                                                    <span style={{ fontSize: 14, fontWeight: 600 }}>{ref.name}</span>
                                                ) : (
                                                    <input
                                                        className="form-input form-input-sm"
                                                        value={editRefNameInput}
                                                        onChange={e => setEditRefNameInput(e.target.value)}
                                                        style={{ height: 28, fontSize: 12, padding: '0 8px', width: 160 }}
                                                    />
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 8px' }}>
                                                {editingRefId !== ref._id ? (
                                                    <span style={{
                                                        fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
                                                        color: 'var(--primary)', background: 'var(--bg-elevated)',
                                                        padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)'
                                                    }}>{ref.value}</span>
                                                ) : (
                                                    <input
                                                        className="form-input form-input-sm"
                                                        value={editRefValueInput}
                                                        onChange={e => setEditRefValueInput(e.target.value)}
                                                        style={{ height: 28, fontSize: 12, padding: '0 8px', width: 120 }}
                                                    />
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 8px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                                                {ref._id}
                                            </td>
                                            <td style={{ padding: '14px 8px', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleToggleRefVisibility(ref._id)}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        color: ref.companyVisible ? 'var(--primary)' : 'var(--text-muted)',
                                                        padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        margin: '0 auto'
                                                    }}
                                                    title={t('plc.companyVisible')}
                                                >
                                                    {ref.companyVisible ? <MdVisibility size={20} /> : <MdVisibilityOff size={20} />}
                                                </button>
                                            </td>
                                            <td style={{ padding: '14px 8px', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                    {editingRefId === ref._id ? (
                                                        <>
                                                            <button onClick={() => handleUpdateRefValue(ref._id)} className="btn btn-success btn-xs" style={{ height: 28 }}>
                                                                <MdSave />
                                                            </button>
                                                            <button onClick={() => setEditingRefId(null)} className="btn btn-secondary btn-xs" style={{ height: 28 }}>
                                                                <MdCancel />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={() => { setEditingRefId(ref._id); setEditRefNameInput(ref.name); setEditRefValueInput(ref.value); }}
                                                            className="btn btn-secondary btn-xs btn-icon"
                                                            title={t('common.edit')}
                                                        >
                                                            <MdEdit />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeleteRefValue(ref._id)}
                                                        className="btn btn-danger btn-xs btn-icon"
                                                        title={t('common.delete')}
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Sensor Calibration Card — spans the full row width instead of falling into the narrow first column */}
                <div className="card" style={{ padding: 24, gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MdScience style={{ color: 'var(--primary)' }} />
                            {t('plc.sensorCalibration')}
                        </h3>
                        <span className="badge badge-primary">{plcCard.calibrationSensors?.length || 0} {t('common.records')}</span>
                    </div>

                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                        {t('plc.sensorCalibrationDesc')}
                    </p>

                    {systemSensors.length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                            {t('plc.noSensorsForCalibration')}
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                                        <th style={{ padding: '12px 8px', width: 32 }}></th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('common.sensor')}</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('plc.calibrationStatus')}</th>
                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', textAlign: 'right' }}>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {systemSensors.map((sensor) => {
                                        const entry = (plcCard.calibrationSensors || []).find(
                                            e => (e.sensorId?._id || e.sensorId) === sensor._id
                                        );
                                        const isSelected = !!entry;
                                        const activeSteps = isSelected ? countActiveCalibrationSteps(entry) : 0;
                                        return (
                                            <tr key={sensor._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                                                <td style={{ padding: '14px 8px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        disabled={addingCalibrationSensorId === sensor._id}
                                                        onChange={() => handleToggleCalibrationSensor(sensor, isSelected, entry?._id)}
                                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '14px 8px' }}>
                                                    <div style={{ fontSize: 14, fontWeight: 600 }}>{sensor.sensorName}</div>
                                                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sensor.unit}</div>
                                                </td>
                                                <td style={{ padding: '14px 8px' }}>
                                                    {isSelected ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <div style={{
                                                                width: 8, height: 8, borderRadius: '50%',
                                                                background: activeSteps > 0 ? 'var(--success)' : 'var(--text-muted)',
                                                                boxShadow: activeSteps > 0 ? '0 0 8px var(--success)' : 'none'
                                                            }} />
                                                            <span style={{ fontSize: 12, fontWeight: 700, color: activeSteps > 0 ? 'var(--success)' : 'var(--text-secondary)' }}>
                                                                {activeSteps > 0
                                                                    ? `${activeSteps}/3 ${t('plc.stepActive')}`
                                                                    : t('plc.calibrationSelected')}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('plc.calibrationNotSelected')}</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '14px 8px', textAlign: 'right' }}>
                                                    {isSelected ? (
                                                        <button
                                                            className="btn btn-primary btn-xs"
                                                            onClick={() => setCalibratingEntry({ entry, sensorName: sensor.sensorName })}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                                                        >
                                                            <MdScience size={14} />
                                                            {t('plc.goToCalibration')}
                                                        </button>
                                                    ) : (
                                                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>

            {calibratingEntry && (
                <SensorCalibrationModal
                    plcId={plcId}
                    entry={calibratingEntry.entry}
                    sensorName={calibratingEntry.sensorName}
                    onClose={() => setCalibratingEntry(null)}
                    onUpdate={handleCalibrationUpdate}
                />
            )}
        </div>
    );
}
